/**
 * PATH: packages/job-hub/src/jobs/tabs/job-qa/itp-export/qaItpExportReadiness.ts
 * FILE_ID: JOBHUB-QA-ITP-EXPORT-READINESS-001
 * PURPOSE: Pre-send checks for ITP Export, same shape as Quote Builder readiness.
 */
import type { ExportReadiness, ExportReadinessCheck } from '@quotech/quote-builder/export/readiness'

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export function computeItpExportReadiness(input: {
  to: string
  subject: string
  body: string
  clientName: string
  reportTitle: string
  checkCount: number
  signedStale: boolean
  hasSigned?: boolean
}): ExportReadiness {
  const recipients = input.to.split(/[,;\n]+/).map((part) => part.trim()).filter(Boolean)
  const checks: ExportReadinessCheck[] = [
    {
      ok: recipients.length > 0 && recipients.every((address) => EMAIL_RE.test(address)),
      severity: 'blocker',
      message: 'Recipient email is missing or invalid.',
    },
    {
      ok: input.checkCount > 0,
      severity: 'warning',
      message: 'The ITP has no checks. Pick a checklist or select checks first.',
    },
    {
      ok: input.subject.trim() !== '',
      severity: 'warning',
      message: 'Subject is missing.',
    },
    {
      ok: input.body.trim() !== '',
      severity: 'warning',
      message: 'Message is empty.',
    },
    {
      ok: input.clientName.trim() !== '',
      severity: 'warning',
      message: 'Client name is missing.',
    },
    {
      ok: input.reportTitle.trim() !== '',
      severity: 'warning',
      message: 'Report title is missing.',
    },
  ]
  if (input.hasSigned || input.signedStale) {
    checks.push({
      ok: !input.signedStale,
      severity: 'warning',
      message: 'You changed the document after signing. Sign again, or remove the signature to send the edited original.',
    })
  }
  const completedChecks = checks.filter((check) => check.ok).length
  return {
    completionPct: Math.round((completedChecks / checks.length) * 100),
    completedChecks,
    totalChecks: checks.length,
    blockers: checks.filter((check) => !check.ok && check.severity === 'blocker').map((check) => check.message),
    warnings: checks.filter((check) => !check.ok && check.severity === 'warning').map((check) => check.message),
    checks,
  }
}
